/**
 * Recipient search/autocomplete (§A) — type a name to filter the Vari directory
 * (loaded once, filtered client-side), or just type a custom name. Picking a
 * person fills the name + email via `onPick`; free typing flows through
 * `onChange` so a non-directory contact still works. Mirrors AddressAutocomplete
 * (combobox + keyboard nav + outside-click close).
 *
 * With `allowCustomEmail`, typing a raw email address (no directory match) offers
 * a "use this email" option (and Enter commits it) via `onPick` with a synthetic
 * recipient whose name + email are the typed address — for ad-hoc stakeholders.
 */
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Mail, User } from 'lucide-react';
import type { Recipient } from '@/api/recipients';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/cn';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function RecipientAutocomplete({
  value,
  onChange,
  onPick,
  recipients,
  placeholder,
  className,
  allowCustomEmail = false,
}: {
  value: string;
  onChange: (name: string) => void;
  onPick: (r: Recipient) => void;
  recipients: Recipient[];
  placeholder?: string;
  className?: string;
  /** Offer a "use this email" option when the text is a raw email with no match. */
  allowCustomEmail?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const listId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // The menu is portaled to <body> with fixed positioning so it can overflow
  // past a clipping ancestor (e.g. a modal's `overflow-auto` panel). null until
  // the first measure.
  const [menu, setMenu] = useState<{
    left: number;
    width: number;
    top?: number;
    bottom?: number;
  } | null>(null);

  const matches = useMemo(() => {
    const q = value.trim().toLowerCase();
    const pool = q
      ? recipients.filter(
          (r) =>
            r.display_name.toLowerCase().includes(q) ||
            r.email.toLowerCase().includes(q) ||
            (r.job_title ?? '').toLowerCase().includes(q),
        )
      : recipients;
    return pool.slice(0, 25);
  }, [value, recipients]);

  const trimmed = value.trim();
  const customEmail =
    allowCustomEmail &&
    EMAIL_RE.test(trimmed) &&
    !matches.some((m) => m.email.toLowerCase() === trimmed.toLowerCase())
      ? trimmed
      : null;

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node;
      // The menu lives in a portal outside wrapRef, so check it too.
      if (wrapRef.current?.contains(t) || listRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function choose(r: Recipient) {
    onPick(r);
    setOpen(false);
    setActive(-1);
  }

  function commitCustom() {
    if (!customEmail) return;
    onPick({ id: '', display_name: customEmail, email: customEmail, job_title: null });
    setOpen(false);
    setActive(-1);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    // Enter commits an active match, else a typed custom email (if allowed).
    if (e.key === 'Enter') {
      if (open && active >= 0 && matches[active]) {
        e.preventDefault();
        choose(matches[active]!);
        return;
      }
      if (customEmail) {
        e.preventDefault();
        commitCustom();
        return;
      }
    }
    if (!open || matches.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (a + 1) % matches.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (a <= 0 ? matches.length - 1 : a - 1));
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  const showList = open && (matches.length > 0 || !!customEmail);

  // Anchor the portaled menu to the input, flipping above when there isn't room
  // below. Re-measures on scroll (capture, so it tracks a scrolling modal) and
  // resize while open.
  useLayoutEffect(() => {
    if (!showList) {
      setMenu(null);
      return;
    }
    function place() {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const GAP = 4;
      const MENU_MAX = 256; // matches max-h-64
      const spaceBelow = window.innerHeight - r.bottom;
      const flipUp = spaceBelow < Math.min(MENU_MAX, 200) && r.top > spaceBelow;
      setMenu(
        flipUp
          ? { left: r.left, width: r.width, bottom: window.innerHeight - r.top + GAP }
          : { left: r.left, width: r.width, top: r.bottom + GAP },
      );
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [showList, matches.length, customEmail]);

  return (
    <div ref={wrapRef} className="relative">
      <Input
        value={value}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {showList &&
        menu &&
        createPortal(
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          style={{
            position: 'fixed',
            left: menu.left,
            width: menu.width,
            top: menu.top,
            bottom: menu.bottom,
          }}
          className="z-[60] max-h-64 overflow-auto rounded-md border border-line bg-bg shadow-lg text-sm"
        >
          {matches.map((r, i) => (
            <li
              key={r.id}
              role="option"
              aria-selected={i === active}
              // onMouseDown (not onClick) so the pick registers before blur.
              onMouseDown={(e) => {
                e.preventDefault();
                choose(r);
              }}
              onMouseEnter={() => setActive(i)}
              className={cn(
                'flex items-start gap-2 px-3 py-2 cursor-pointer',
                i === active ? 'bg-fill-soft' : 'hover:bg-fill-soft',
              )}
            >
              <User className="w-3.5 h-3.5 mt-0.5 shrink-0 text-ink-faint" />
              <span className="min-w-0">
                <span className="block font-medium truncate">{r.display_name}</span>
                <span className="block text-xs text-ink-faint truncate">
                  {[r.job_title, r.email].filter(Boolean).join(' · ')}
                </span>
              </span>
            </li>
          ))}
          {customEmail && (
            <li
              role="option"
              aria-selected={false}
              onMouseDown={(e) => {
                e.preventDefault();
                commitCustom();
              }}
              className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-fill-soft border-t border-line"
            >
              <Mail className="w-3.5 h-3.5 shrink-0 text-ink-faint" />
              <span className="text-sm">
                Use <span className="font-medium">{customEmail}</span>
              </span>
            </li>
          )}
        </ul>,
          document.body,
        )}
    </div>
  );
}
